import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, LayoutDashboard, ShieldCheck, User, Users, Package, X } from 'lucide-react';
import { toast } from 'sonner';
import { clearSession } from '@/lib/authSession';

const Navbar = ({ user }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMobileMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = () => {
    clearSession();
    toast.success('Logged out successfully');
    window.location.href = '/';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-[#d4a574]/20" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <div
          className="h-10 sm:h-11 w-40 sm:w-56 overflow-hidden cursor-pointer select-none"
          onClick={() => navigate('/')}
          data-testid="logo"
          title="Wearoclub Home"
        >
          <img
            src="/logo.png"
            alt="Wearoclub"
            className="h-full w-full object-cover object-center"
            onError={(e) => {
              // Fallback to text if logo missing
              const parent = e.currentTarget.parentElement;
              if (parent) parent.innerHTML = '<span class=\"text-2xl font-bold gradient-text\">Wearoclub</span>';
            }}
          />
        </div>

        {/* Mobile: profile button only */}
        <div className="md:hidden flex items-center gap-4">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-full bg-[#1a1a1a] border border-[#d4a574]/30 text-gray-200 hover:bg-[#222]"
            aria-label="Open menu"
            data-testid="mobile-profile-btn"
          >
            <User className="w-5 h-5" />
          </button>
        </div>

        {/* Desktop/Tablet navigation */}
        <div className="hidden md:flex items-center gap-6">
          <button
            onClick={() => navigate('/designers')}
            className="flex items-center gap-2 text-gray-300 hover:text-[#d4a574] transition-colors"
            data-testid="nav-designers-link"
          >
            <Users className="w-4 h-4" />
            Designers
          </button>
          <button
            onClick={() => navigate('/products')}
            className="flex items-center gap-2 text-gray-300 hover:text-[#d4a574] transition-colors"
            data-testid="nav-products-link"
            title="Go to Products Page"
          >
            <Package className="w-4 h-4" />
            Products
          </button>
          {user ? (
            <>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-gray-300 hover:text-[#d4a574] transition-colors"
                data-testid="nav-dashboard-link"
                title="Manage Your Account & Bookings"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </button>

              {user.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2 text-gray-300 hover:text-[#d4a574] transition-colors"
                  data-testid="nav-admin-link"
                  title="You are Admin: Manage Bookings and Orders"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Admin
                </button>
              )}

              <div className="flex items-center gap-4">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-gray-300 hover:text-red-400 transition-colors"
                  data-testid="logout-btn"
                  title="Bye! See You Next Time"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => navigate('/auth')}
              className="btn-primary"
              data-testid="nav-login-btn"
              title="Login to Your Account"
            >
              Login
            </button>
          )}
        </div>
      </div>

      {/* Mobile slide-over menu */}
      <div
        className={`fixed inset-0 z-50 md:hidden ${mobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!mobileMenuOpen}
        data-testid="mobile-drawer"
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-drawer-overlay"
        />
        {/* Panel */}
        <div
          className={`absolute right-0 top-0 h-full w-72 glass-card rounded-none border-l border-[#d4a574]/20 shadow-xl transform transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="p-4 flex items-center justify-between border-b border-[#d4a574]/20">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-[#1a1a1a]/70 backdrop-blur-xl border border-[#d4a574]/30">
                <User className="w-5 h-5 text-gray-200" />
              </div>
              <div className="text-sm">
                <div className="text-white font-medium">{user ? user.name : 'Guest'}</div>
                {user && <div className="text-gray-400 text-xs">Signed in</div>}
              </div>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
              className="p-2 rounded-md text-gray-300 hover:text-white"
              data-testid="mobile-drawer-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4">
            <div className="glass-card rounded-lg border-[#d4a574]/20 p-2 space-y-2">
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/designers'); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-[#1a1a1a] text-gray-200"
                data-testid="mobile-drawer-designers-link"
              >
                <Users className="w-4 h-4" />
                Designers
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/products'); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-[#1a1a1a] text-gray-200"
                data-testid="mobile-drawer-products-link"
              >
                <Package className="w-4 h-4" />
                Products
              </button>
              {user ? (
                <>
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/dashboard'); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-[#1a1a1a] text-gray-200"
                    data-testid="mobile-drawer-dashboard-link"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </button>
                  {user.role === 'admin' && (
                    <button
                      onClick={() => { setMobileMenuOpen(false); navigate('/admin'); }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-[#1a1a1a] text-gray-200"
                      data-testid="mobile-drawer-admin-link"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Admin
                    </button>
                  )}
                  <button
                    onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-red-500/10 text-red-400"
                    data-testid="mobile-drawer-logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/auth'); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-[#1a1a1a] text-gray-200"
                  data-testid="mobile-drawer-login-btn"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
